import React from "react";
import { Link } from "react-router-dom";
import { useI18n } from "@/i18n/I18nContext";
import { IMAGES, ROOMS, CONTACT } from "@/data/site";
import { Reveal } from "@/components/Reveal";
import { ArrowRight, Star, MapPin, Wifi, Snowflake, Coffee, Tv, Check, Sun, Users } from "lucide-react";
import { Button } from "@/components/ui/button";
import { FAQSection } from "@/components/FAQSection";
import { getReviews } from "@/data/reviews";

const Hero = () => {
  const { t, lang } = useI18n();
  return (
    <section data-testid="hero-section" className="relative min-h-[95vh] flex items-end overflow-hidden">
      <div className="absolute inset-0">
        <img src={IMAGES.hero[0]} alt="Bellagio Lake Como" className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/25 to-black/55" />
        <div className="absolute inset-x-0 bottom-0 h-40 md:h-56 bg-gradient-to-b from-transparent to-[#FAF9F6] pointer-events-none" />
      </div>
      <div className="relative max-w-7xl mx-auto px-6 md:px-12 pb-20 md:pb-32 w-full">
        <div className="max-w-3xl text-white">
          <div className="text-xs md:text-sm uppercase tracking-[0.3em] text-white/90 mb-6 flex items-center gap-2">
            <MapPin className="w-4 h-4" /> {t("hero.eyebrow")}
          </div>
          <h1 data-testid="hero-title" className="font-display text-5xl sm:text-6xl lg:text-7xl leading-[1.05] tracking-tight whitespace-pre-line">
            {t("hero.title")}
          </h1>
          <p className="mt-6 max-w-xl text-base md:text-lg text-white/95 leading-relaxed" style={{textShadow: "0 1px 3px rgba(0,0,0,0.4)"}}>
            {t("hero.subtitle")}
          </p>
          <div className="mt-10 flex flex-wrap gap-4">
            <Link data-testid="hero-cta-primary" to={`/${lang}/prenota`}
              className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full bg-white text-[#1A1A1A] font-medium hover:bg-[#1D3557] hover:text-white transition-all">
              {t("hero.cta")} <ArrowRight className="w-4 h-4" />
            </Link>
            <Link data-testid="hero-cta-secondary" to={`/${lang}/camere`}
              className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full border border-white/40 text-white hover:bg-white/10 transition-all">
              {t("hero.ctaSecondary")}
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
};

const Welcome = () => {
  const { t } = useI18n();
  return (
    <section className="py-24 md:py-32 bg-[#FAF9F6]">
      <div className="max-w-5xl mx-auto px-6 md:px-12 text-center">
        <Reveal>
          <div className="text-xs uppercase tracking-[0.3em] text-[#345B73] mb-5">
            Bellagio · Lago di Como
          </div>
          <h2 className="font-display text-4xl md:text-5xl tracking-tight text-[#1A1A1A] leading-tight">
            {t("home.welcomeTitle")}
          </h2>
          <p className="mt-6 text-lg md:text-xl text-[#5C5C5C] leading-relaxed max-w-3xl mx-auto">
            {t("home.welcomeText")}
          </p>
        </Reveal>
      </div>
    </section>
  );
};

const Breakfast = () => {
  const { t, lang } = useI18n();
  const points = t("home.breakfastPoints");
  return (
    <section data-testid="breakfast-section" className="py-24 md:py-32 bg-[#F2EFEB]">
      <div className="max-w-7xl mx-auto px-6 md:px-12 grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
        <Reveal>
          <div className="grid grid-cols-2 gap-3 md:gap-4">
            <div className="col-span-2 aspect-[16/10] overflow-hidden rounded-2xl">
              <img src={IMAGES.breakfast[0]} alt="Colazione I Lake It Bellagio" className="w-full h-full object-cover img-zoom" />
            </div>
            <div className="aspect-square overflow-hidden rounded-2xl"><img src={IMAGES.breakfast[1]} alt="" className="w-full h-full object-cover img-zoom" /></div>
            <div className="aspect-square overflow-hidden rounded-2xl"><img src={IMAGES.breakfast[2]} alt="" className="w-full h-full object-cover img-zoom" /></div>
          </div>
        </Reveal>
        <Reveal delay={150}>
          <div>
            <div className="flex items-center gap-2 text-xs uppercase tracking-[0.3em] text-[#345B73] mb-4">
              <Coffee className="w-4 h-4" /> {t("home.breakfastEyebrow")}
            </div>
            <h2 className="font-display text-4xl md:text-5xl text-[#1A1A1A] tracking-tight">{t("home.breakfastTitle")}</h2>
            <p className="mt-5 text-[#5C5C5C] text-base md:text-lg leading-relaxed">{t("home.breakfastText")}</p>
            <ul className="mt-8 grid sm:grid-cols-2 gap-3">
              {points.map((p, i) => (
                <li key={i} className="flex items-center gap-2 text-sm text-[#1A1A1A]">
                  <Check className="w-4 h-4 text-[#345B73] flex-shrink-0" /> {p}
                </li>
              ))}
            </ul>
            <Link to={`/${lang}/colazione`} data-testid="home-breakfast-cta"
              className="mt-8 inline-flex items-center gap-2 px-7 py-3 rounded-full bg-[#1D3557] text-white font-medium hover:bg-[#345B73] transition-colors">
              {t("home.breakfastCta")} <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </Reveal>
      </div>
    </section>
  );
};

const RoomsTeaser = () => {
  const { t, lang } = useI18n();
  return (
    <section data-testid="rooms-teaser" className="py-20 md:py-28 bg-[#F2EFEB]">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <Reveal>
          <div className="text-center mb-16">
            <h2 className="font-display text-4xl md:text-5xl tracking-tight text-[#1A1A1A]">{t("home.roomsTeaser")}</h2>
            <p className="mt-4 text-[#5C5C5C] max-w-xl mx-auto">{t("home.roomsTeaserSub")}</p>
          </div>
        </Reveal>
        <div className="grid md:grid-cols-3 gap-6 md:gap-8">
          {ROOMS.map((room, idx) => (
            <Reveal key={room.slug} delay={idx * 100}>
              <Link to={`/${lang}/camere#${room.slug}`} data-testid={`room-card-${room.slug}`}
                className="group block bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-xl transition-all duration-500">
                <div className="aspect-[4/5] overflow-hidden relative">
                  <img src={IMAGES.rooms[room.slug][0]} alt={t(`rooms.${room.slug.split("_")[0]}.name`)}
                    className="w-full h-full object-cover img-zoom" />
                  {room.slug === "blu_marino" && (
                    <span data-testid={`badge-exclusive-terrace-${room.slug}`}
                      className="absolute top-4 left-4 z-10 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/90 backdrop-blur text-[#1D3557] text-xs font-medium shadow-sm">
                      <Sun className="w-3.5 h-3.5" /> {t("rooms.exclusiveTerraceBadge")}
                    </span>
                  )}
                  {room.slug === "rosso_granato" && (
                    <span data-testid={`badge-family-${room.slug}`}
                      className="absolute top-4 left-4 z-10 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/90 backdrop-blur text-[#1D3557] text-xs font-medium shadow-sm">
                      <Users className="w-3.5 h-3.5" /> {t("rooms.familyFriendlyBadge")}
                    </span>
                  )}
                </div>
                <div className="p-8">
                  <div className="flex items-center gap-2 mb-3">
                    <span className="w-3 h-3 rounded-full" style={{ background: room.color }} />
                    <span className="text-xs uppercase tracking-widest text-[#5C5C5C]">{t(`rooms.${room.slug.split("_")[0]}.tag`)}</span>
                  </div>
                  <h3 className="font-display text-2xl text-[#1A1A1A] group-hover:text-[#345B73] transition-colors">
                    {t(`rooms.${room.slug.split("_")[0]}.name`)}
                  </h3>
                  <p className="mt-3 text-sm text-[#5C5C5C] leading-relaxed line-clamp-3">
                    {t(`rooms.${room.slug.split("_")[0]}.desc`)}
                  </p>
                  <div className="mt-6 inline-flex items-center gap-2 text-sm text-[#345B73] font-medium">
                    {t("common.readMore")} <ArrowRight className="w-4 h-4" />
                  </div>
                </div>
              </Link>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
};

const Strengths = () => {
  const { t } = useI18n();
  const icons = [Star, MapPin, Coffee, Wifi];
  const items = t("home.strengths");
  return (
    <section className="py-24 md:py-32 bg-[#FAF9F6]">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <Reveal>
          <h2 className="font-display text-4xl md:text-5xl text-[#1A1A1A] tracking-tight mb-16 max-w-2xl">{t("home.strengthsTitle")}</h2>
        </Reveal>
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
          {items.map((s, i) => {
            const Icon = icons[i] || Star;
            return (
              <Reveal key={i} delay={i * 80}>
                <div className="p-8 rounded-2xl border border-black/5 bg-white hover:bg-[#F2EFEB] transition-colors h-full">
                  <Icon className="w-7 h-7 text-[#345B73] mb-5" />
                  <h3 className="font-display text-xl text-[#1A1A1A] mb-3">{s.t}</h3>
                  <p className="text-sm text-[#5C5C5C] leading-relaxed">{s.d}</p>
                </div>
              </Reveal>
            );
          })}
        </div>
      </div>
    </section>
  );
};

const Gallery = () => {
  const { t } = useI18n();
  return (
    <section className="py-24 md:py-32 bg-[#FAF9F6]">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <Reveal>
          <h2 className="font-display text-4xl md:text-5xl text-[#1A1A1A] tracking-tight mb-12">{t("home.galleryTitle")}</h2>
        </Reveal>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
          {IMAGES.gallery.map((src, i) => (
            <Reveal key={src} delay={i * 60}>
              <div className={`overflow-hidden rounded-2xl ${i === 0 || i === 3 ? "row-span-2 md:aspect-[3/4]" : "aspect-square"}`}>
                <img src={src} alt="" className="w-full h-full object-cover img-zoom" />
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
};

const ReviewsTeaser = () => {
  const { t, lang } = useI18n();
  const { reviews, average } = getReviews(lang);
  if (!reviews?.length) return null;
  const top = reviews.slice(0, 3);
  return (
    <section className="py-24 md:py-32 bg-[#F2EFEB]">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <Reveal>
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-12">
            <div>
              <h2 className="font-display text-4xl md:text-5xl text-[#1A1A1A] tracking-tight">{t("home.reviewsTeaser")}</h2>
              {average && (
                <div className="mt-4 flex items-center gap-3">
                  <div className="flex">
                    {[1,2,3,4,5].map(n => <Star key={n} className="w-5 h-5 fill-[#E6B800] stroke-[#E6B800]" />)}
                  </div>
                  <span className="text-[#1A1A1A] font-medium">{average}/5</span>
                </div>
              )}
            </div>
            <Link to={`/${lang}/recensioni`} data-testid="reviews-teaser-cta"
              className="inline-flex items-center gap-2 text-[#345B73] font-medium hover:gap-3 transition-all">
              {t("common.readMore")} <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </Reveal>
        <div className="grid md:grid-cols-3 gap-6">
          {top.map((r, i) => (
            <Reveal key={r.id} delay={i * 100}>
              <div className="bg-white p-8 rounded-2xl h-full">
                <div className="flex mb-4">
                  {[1,2,3,4,5].map(n => <Star key={n} className={`w-4 h-4 ${n <= r.rating ? "fill-[#E6B800] stroke-[#E6B800]" : "stroke-[#ddd]"}`} />)}
                </div>
                <h3 className="font-display text-lg text-[#1A1A1A] mb-3">{r.title}</h3>
                <p className="text-sm text-[#5C5C5C] leading-relaxed mb-5 line-clamp-4">"{r.text}"</p>
                <div className="flex items-center justify-between text-sm">
                  <span className="font-medium text-[#1A1A1A]">{r.name}</span>
                  <span className="text-[#5C5C5C] text-xs uppercase tracking-wide">{r.source === "airbnb" ? "Airbnb" : r.source === "google" ? "Google" : "Booking.com"}</span>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
};

const MapTeaser = () => {
  const { t, lang } = useI18n();
  return (
    <section className="py-24 md:py-32 bg-[#FAF9F6]">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <Reveal>
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="font-display text-4xl md:text-5xl text-[#1A1A1A] tracking-tight">{t("home.mapTeaser")}</h2>
              <p className="mt-4 text-[#5C5C5C] text-lg">{t("home.mapSub")}</p>
              <div className="mt-8 flex flex-wrap gap-4">
                <a href={`https://www.google.com/maps/dir/?api=1&destination=${CONTACT.mapsQuery}`}
                   target="_blank" rel="noopener noreferrer" data-testid="map-directions-btn"
                   className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-[#345B73] text-white font-medium hover:bg-[#2B4A5D] transition-all">
                  <MapPin className="w-4 h-4" /> {t("common.openMaps")}
                </a>
                <Link to={`/${lang}/come-arrivare`} data-testid="map-details-btn"
                  className="inline-flex items-center gap-2 px-6 py-3 rounded-full border border-[#345B73] text-[#345B73] hover:bg-[#345B73]/5 transition-all">
                  {t("common.learnMore")}
                </Link>
              </div>
            </div>
            <div className="aspect-[4/3] rounded-2xl overflow-hidden shadow-sm">
              <iframe
                title="map"
                src={`https://www.google.com/maps?q=${CONTACT.mapsQuery}&output=embed`}
                className="w-full h-full border-0"
                loading="lazy"
              />
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
};

export default function HomePage() {
  return (
    <>
      <Hero />
      <Welcome />
      <Breakfast />
      <RoomsTeaser />
      <Strengths />
      <Gallery />
      <ReviewsTeaser />
      <MapTeaser />
      <FAQSection compact />
    </>
  );
}
